import { useQueryClient } from '@tanstack/react-query';
import { FC, MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as RestartIcon } from '@/assets/icons/restart-icon.svg';
import { ReactComponent as StopIcon } from '@/assets/icons/stop-circle.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import OptimisationStatusBadge from '@/pages/OptimisationProject/components/OptimisationStatusBadge';
import { OptimisationProjectResource } from '@/types/resources';
import { api } from '@/utils/api';
import {
  OptimisationProjectStatusEnum,
  OptimisationTestTypeEnum,
} from '@/utils/enums';

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
  margin-bottom: 32px;
`;

const StyledText = styled(Text)`
  margin-top: 6px;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 142px;
`;

interface Props {
  project: OptimisationProjectResource;
}

const OptimisationTableRow: FC<Props> = ({ project }) => {
  const { teamId } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const handleRowClick = (id: number) => {
    navigate(`/teams/${teamId}/optimisation-projects/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to delete project?`,
        `${project.projectId ? project.projectId + ' / ' : ''}${project!.name}`,
      );

      if (!confirmed) return;

      await api.delete<never, { id: number }>(
        `/v1/optimisation-tests/${project.id}`,
      );

      queryClient.invalidateQueries(['optimisation_tests'], { exact: false });

      alerts.success('Project was successfully deleted');
    } catch (err) {
      alerts.error();
    }
  };

  const onProjectEnd = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to stop project??`,
        `${project.projectId} / ${project!.name}`,
      );

      if (!confirmed) return;

      await api.put<
        never,
        { id: number; status: OptimisationProjectStatusEnum }
      >(`/v1/optimisation-tests/${project.id}/status`, {
        status: OptimisationProjectStatusEnum.ENDED,
      });

      queryClient.invalidateQueries(['optimisation_tests'], { exact: false });

      alerts.success('Project was successfully stoped');
    } catch (err) {
      alerts.error();
    }
  };

  const onProjectStart = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to start the project?`,
        `${project!.projectId} / ${project!.name}`,
      );

      if (!confirmed) return;

      await api.put<
        never,
        { id: number; status: OptimisationProjectStatusEnum }
      >(`/v1/optimisation-tests/${project!.id}/status`, {
        status: OptimisationProjectStatusEnum.RUNNING,
      });

      queryClient.invalidateQueries(['optimisation_tests'], { exact: false });

      alerts.success('Project has been successfully started');
    } catch (err) {
      alerts.error();
    }
  };

  const getTestsCategoryCount = (type: OptimisationTestTypeEnum) => {
    return project?.testsCounts
      .filter((testCount) => testCount.type === type)
      .reduce(
        (previousValue, currentValue) => previousValue + currentValue.count,
        0,
      );
  };

  const getTestsWorstStatus = (type: OptimisationTestTypeEnum) => {
    return Math.min(
      ...project?.testsCounts
        .filter((testCount) => testCount.type === type)
        .map((count) => count.status),
    );
  };

  const thumbsTestsCount = getTestsCategoryCount(
    OptimisationTestTypeEnum.THUMBS,
  );

  const tipsTestsCount = getTestsCategoryCount(OptimisationTestTypeEnum.TIPS);

  const sequencesTestsCount = getTestsCategoryCount(
    OptimisationTestTypeEnum.SEQUENCES,
  );

  const introsTestsCount = getTestsCategoryCount(
    OptimisationTestTypeEnum.INTRO,
  );

  const thumbsMinStatus = getTestsWorstStatus(OptimisationTestTypeEnum.THUMBS);

  const tipsMinStatus = getTestsWorstStatus(OptimisationTestTypeEnum.TIPS);

  const sequencesMinStatus = getTestsWorstStatus(
    OptimisationTestTypeEnum.SEQUENCES,
  );

  const introsMinStatus = getTestsWorstStatus(OptimisationTestTypeEnum.INTRO);

  return (
    <Tr onClick={() => handleRowClick(project.id)}>
      <Td>
        <Text $type="smallBold">{project.name}</Text>
        <StyledText $type="extraSmall" $color="gray">
          {project.projectId ?? '—'}
        </StyledText>
      </Td>
      <Td>
        {!!thumbsTestsCount ? (
          <>
            <OptimisationStatusBadge status={thumbsMinStatus} />
            <StyledText $type="extraSmall">
              {project.maxCtr?.toString() ? `CTR: ${project.maxCtr} %` : '—'}
            </StyledText>
            <StyledText $type="extraSmall" $color="gray">
              {thumbsTestsCount} variation{thumbsTestsCount > 1 && 's'}
            </StyledText>
          </>
        ) : (
          <OptimisationStatusBadge />
        )}
      </Td>
      <Td>
        {!!tipsTestsCount ? (
          <>
            <OptimisationStatusBadge status={tipsMinStatus} />
            <StyledText $type="extraSmall">
              {project.maxWatchtime?.toString()
                ? `Watchtime: ${project.maxWatchtime} %`
                : '—'}
            </StyledText>
            <StyledText $type="extraSmall" $color="gray">
              {tipsTestsCount} variation{tipsTestsCount > 1 && 's'}
            </StyledText>
          </>
        ) : (
          <OptimisationStatusBadge />
        )}
      </Td>
      <Td>
        {!!sequencesTestsCount ? (
          <>
            <OptimisationStatusBadge status={sequencesMinStatus} />
            <StyledText $type="extraSmall">
              {project.maxEstimatedFinalValue
                ? `eYTV: ${project.maxEstimatedFinalValue}`
                : '—'}
            </StyledText>
            <StyledText $type="extraSmall" $color="gray">
              {sequencesTestsCount} variation{sequencesTestsCount > 1 && 's'}
            </StyledText>
          </>
        ) : (
          <OptimisationStatusBadge />
        )}
      </Td>
      <Td>
        {!!introsTestsCount ? (
          <>
            <OptimisationStatusBadge status={introsMinStatus} />
            <StyledText $type="extraSmall">
              {project.maxEstimatedFinalValue
                ? `eYTV: ${project.maxEstimatedFinalValue}`
                : '—'}
            </StyledText>
            <StyledText $type="extraSmall" $color="gray">
              {introsTestsCount} variation{introsTestsCount > 1 && 's'}
            </StyledText>
          </>
        ) : (
          <OptimisationStatusBadge />
        )}
      </Td>
      <Td $actionsCol>
        {OptimisationProjectStatusEnum.DRAFT === project.status ? (
          <ActionsWrap>
            <StyledActionsDropdown>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </StyledActionsDropdown>
          </ActionsWrap>
        ) : (
          <ActionsWrap>
            <StyledActionsDropdown>
              {OptimisationProjectStatusEnum.RUNNING === project.status && (
                <ActionsDropdownButton as="button" onClick={onProjectEnd}>
                  <span>
                    <StopIcon />
                  </span>
                  End Project
                </ActionsDropdownButton>
              )}
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
              {OptimisationProjectStatusEnum.ENDED === project.status && (
                <ActionsDropdownButton as="button" onClick={onProjectStart}>
                  <span>
                    <RestartIcon />
                  </span>
                  Start over
                </ActionsDropdownButton>
              )}
            </StyledActionsDropdown>
          </ActionsWrap>
        )}
      </Td>
    </Tr>
  );
};

export default OptimisationTableRow;
